import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { cutMaterialFormSchema, type CutMaterialFormData } from '@/schemas/cutMaterialSchemas';
import type { CutMaterial } from '@/types/legacy';
import { Loader2 } from 'lucide-react';
import logger from '@/lib/logger';

interface CutMaterialFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cutMaterial?: CutMaterial | null;
  onSuccess?: () => void;
}

export function CutMaterialFormDialog({ open, onOpenChange, cutMaterial, onSuccess }: CutMaterialFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [workOrders, setWorkOrders] = useState<any[]>([]);

  const form = useForm<CutMaterialFormData>({
    resolver: zodResolver(cutMaterialFormSchema),
    defaultValues: {
      material_name: cutMaterial?.material_name || '',
      material_type: cutMaterial?.material_type || '',
      dimensions: cutMaterial?.dimensions || '',
      thickness: cutMaterial?.thickness || '',
      quantity: cutMaterial?.quantity || 1,
      unit: cutMaterial?.unit || 'units',
      batch_number: cutMaterial?.batch_number || '',
      cut_date: cutMaterial?.cut_date || '',
      location: cutMaterial?.location || '',
      assigned_to_work_order_id: cutMaterial?.assigned_to_work_order_id || '',
      notes: cutMaterial?.notes || '',
      status: cutMaterial?.status || 'available',
      is_active: cutMaterial?.is_active ?? true,
    },
  });

  useEffect(() => {
    if (open) {
      fetchWorkOrders();
    }
  }, [open]);

  const fetchWorkOrders = async () => {
    try {
      const { data, error } = await api
        .from('work_orders')
        .select('id, order_number, product_name')
        .order('order_number', { ascending: false })
        .limit(50);

      if (error) throw error;
      setWorkOrders(data || []);
    } catch (error: any) {
      logger.error('Error fetching work orders:', error);
    }
  };

  const onSubmit = async (data: CutMaterialFormData) => {
    setLoading(true);
    try {
      const payload = {
        material_name: data.material_name,
        material_type: data.material_type || null,
        dimensions: data.dimensions || null,
        thickness: data.thickness || null,
        quantity: data.quantity,
        unit: data.unit,
        batch_number: data.batch_number || null,
        cut_date: data.cut_date || null,
        location: data.location || null,
        assigned_to_work_order_id: data.assigned_to_work_order_id || null,
        notes: data.notes || null,
        status: data.status,
        is_active: data.is_active,
      };

      if (cutMaterial) {
        const { error } = await api
          .from('cut_material')
          .update(payload)
          .eq('id', cutMaterial.id);

        if (error) throw error;

        toast({
          title: 'Material cortado actualizado',
          description: 'El material se actualizó correctamente',
        });
      } else {
        const { error } = await api
          .from('cut_material')
          .insert([payload]);

        if (error) throw error;

        toast({
          title: 'Material cortado creado',
          description: 'El material se creó correctamente',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['cut_material'] });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el material',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{cutMaterial ? 'Editar Material Cortado' : 'Nuevo Material Cortado'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="material_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre del Material *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Fibra de Carbono" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="material_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: carbon_fiber, fiberglass, etc." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="dimensions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dimensiones</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: 1000x500mm" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="thickness"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Grosor</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: 2mm" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cantidad *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : 1)}
                        step="0.001"
                        min={0.001}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unidad *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="units, m2, etc." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="available">Disponible</SelectItem>
                        <SelectItem value="reserved">Reservado</SelectItem>
                        <SelectItem value="used">Usado</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="batch_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número de Lote</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: BATCH-2025-001" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cut_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha de Corte</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ubicación</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ubicación en almacén" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="assigned_to_work_order_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Asignado a Orden de Trabajo</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar orden (opcional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="__none__">Ninguna</SelectItem>
                      {workOrders.map((wo) => (
                        <SelectItem key={wo.id} value={wo.id}>
                          {wo.order_number} - {wo.product_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Orden de trabajo a la que está asignado este material
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Notas adicionales" rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activo</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      El material está activo en el sistema
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {cutMaterial ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
